import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import DailyDiscoveryService from '../../services/dailyDiscovery';
import { searchArtistForProvider } from '../../services/musicProvider';
import { useLanguage } from '../../context/LanguageContext';
import { useTranslation } from '../../i18n/translations';

const DailyDiscovery = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { language } = useLanguage();
  const { t } = useTranslation(language);
  
  const [discovery, setDiscovery] = useState(null);
  const [representativeArtist, setRepresentativeArtist] = useState(null);
  const [loadingArtistImage, setLoadingArtistImage] = useState(false);
  const [allDiscoveries, setAllDiscoveries] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingList, setLoadingList] = useState(false);

  // Carica discovery di oggi e lista completa
  useEffect(() => {
    const loadInitialDiscovery = async () => {
      // Se arriva dal box Home con loadLatest, IGNORA la memoria e carica l'ultimo
      if (state?.loadLatest) {
        console.log('[DailyDiscovery] loadLatest flag detected - loading today discovery');
        // Carica l'ultimo (oggi)
        await loadTodayDiscovery();
        // NON aggiornare la memoria qui - verrà aggiornata dall'altro useEffect
        return;
      }
      
      // Altrimenti usa la memoria: controlla se c'è una discovery salvata nella sessione
      const savedDiscoveryId = sessionStorage.getItem('current_discovery_id');
      console.log('[DailyDiscovery] Checking memory:', savedDiscoveryId);
      
      if (savedDiscoveryId) {
        // Carica la discovery salvata (memoria)
        console.log('[DailyDiscovery] Loading from memory:', savedDiscoveryId);
        await loadDiscoveryById(parseInt(savedDiscoveryId, 10));
      } else {
        // Altrimenti carica quella di oggi
        console.log('[DailyDiscovery] No memory - loading today');
        await loadTodayDiscovery();
      }
    };
    
    loadInitialDiscovery();
    loadAllDiscoveries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [language, state?.loadLatest, state?.timestamp]); // Ricarica quando cambia loadLatest o timestamp

  // Salva la discovery corrente in sessionStorage quando cambia
  useEffect(() => {
    if (discovery?.id) {
      sessionStorage.setItem('current_discovery_id', discovery.id.toString());
    }
  }, [discovery?.id]);

  const loadTodayDiscovery = async () => {
    try {
      const data = await DailyDiscoveryService.getTodayDiscovery(language);
      
      if (!data) {
        console.log('[Daily Discovery] No discovery available');
        return;
      }

      setDiscovery(data);
      
      // Reset immagine precedente e mostra loading
      setRepresentativeArtist(null);
      setLoadingArtistImage(true);

      // Carica solo il primo artista per l'immagine rappresentativa
      if (data.artists && data.artists.length > 0) {
        try {
          // Gestisci sia array di stringhe (vecchio formato) che array di oggetti (nuovo formato)
          const firstArtist = data.artists[0];
          const artistName = typeof firstArtist === 'string' ? firstArtist : firstArtist.name;
          const artistGenre = typeof firstArtist === 'object' ? firstArtist.genre : null;
          const artistOrigin = typeof firstArtist === 'object' ? firstArtist.origin : null;
          const artistSpotifyId = typeof firstArtist === 'object'
            ? (firstArtist.spotify_id || firstArtist.spotifyId || null)
            : null;
          const artistImageUrl = typeof firstArtist === 'object'
            ? (firstArtist.image_url || null)
            : null;

          // Se abbiamo già un'immagine in DB, usala subito (evita lookup esterno inutile)
          if (artistImageUrl) {
            setRepresentativeArtist({
              name: artistName,
              images: [{ url: artistImageUrl }]
            });
            return;
          }
          
          console.log(`[DailyDiscovery] 🔍 Searching provider artist for representative image: "${artistName}"`);
          console.log(`[DailyDiscovery]    Genre hint: ${artistGenre || 'none'}, Origin hint: ${artistOrigin || 'none'}`);
          if (artistSpotifyId) {
            console.log(`[DailyDiscovery]    Using stored provider ID: ${artistSpotifyId}`);
          }
          
          // Usa provider unificato (Spotify/Deezer) con hint per disambiguazione
          const providerData = await searchArtistForProvider(artistName, artistSpotifyId, artistGenre, artistOrigin, true);
          
          if (providerData) {
            console.log(`[DailyDiscovery] ✅ Provider found: "${providerData.name}" (requested: "${artistName}")`);
            if (providerData.name.toLowerCase() !== artistName.toLowerCase()) {
              console.warn(`[DailyDiscovery] ⚠️ NAME MISMATCH: Requested "${artistName}" but got "${providerData.name}"`);
            }
          } else {
            console.warn(`[DailyDiscovery] ❌ Provider found nothing for: "${artistName}"`);
          }
          
          if (providerData) {
            setRepresentativeArtist({
              name: artistName,
              images: providerData.images || []
            });
          }
        } catch (error) {
          console.log('Could not load representative artist');
        } finally {
          setLoadingArtistImage(false);
        }
      } else {
        setLoadingArtistImage(false);
      }

    } catch (error) {
      console.log('[Daily Discovery] Load error:', error.message);
      setLoadingArtistImage(false);
    }
  };

  const loadAllDiscoveries = async () => {
    try {
      setLoadingList(true);
      const data = await DailyDiscoveryService.getAllDiscoveries(language);
      setAllDiscoveries(data);
    } catch (error) {
      console.error('[Daily Discovery] Error loading all discoveries:', error);
    } finally {
      setLoadingList(false);
    }
  };

  const loadDiscoveryById = async (discoveryId) => {
    try {
      const data = await DailyDiscoveryService.getDiscoveryById(discoveryId, language);
      
      if (!data) {
        console.warn('[DailyDiscovery] Discovery not found, loading latest...');
        // Se non esiste più (es. cancellato), carica l'ultimo disponibile
        await loadTodayDiscovery();
        return;
      }

      setDiscovery(data);
      setShowDropdown(false);

      // Reset immagine precedente e mostra placeholder grigio
      setRepresentativeArtist(null);
      setLoadingArtistImage(true);

      // Carica artista rappresentativo
      if (data.artists && data.artists.length > 0) {
        try {
          // Gestisci sia array di stringhe (vecchio formato) che array di oggetti (nuovo formato)
          const firstArtist = data.artists[0];
          const artistName = typeof firstArtist === 'string' ? firstArtist : firstArtist.name;
          const artistGenre = typeof firstArtist === 'object' ? firstArtist.genre : null;
          const artistOrigin = typeof firstArtist === 'object' ? firstArtist.origin : null;
          const artistSpotifyId = typeof firstArtist === 'object'
            ? (firstArtist.spotify_id || firstArtist.spotifyId || null)
            : null;
          const artistImageUrl = typeof firstArtist === 'object'
            ? (firstArtist.image_url || null)
            : null;

          // Se abbiamo già un'immagine in DB, usala subito (evita lookup esterno inutile)
          if (artistImageUrl) {
            setRepresentativeArtist({
              name: artistName,
              images: [{ url: artistImageUrl }]
            });
            return;
          }
          
          console.log(`[DailyDiscovery] 🔍 Searching provider artist: "${artistName}"`);
          console.log(`[DailyDiscovery]    Genre hint: ${artistGenre || 'none'}, Origin hint: ${artistOrigin || 'none'}`);
          if (artistSpotifyId) {
            console.log(`[DailyDiscovery]    Using stored provider ID: ${artistSpotifyId}`);
          }
          
          // Usa provider unificato (Spotify/Deezer) con hint per disambiguazione
          const providerData = await searchArtistForProvider(artistName, artistSpotifyId, artistGenre, artistOrigin, true);
          
          if (providerData) {
            console.log(`[DailyDiscovery] ✅ Provider found: "${providerData.name}" (requested: "${artistName}")`);
            if (providerData.name.toLowerCase() !== artistName.toLowerCase()) {
              console.warn(`[DailyDiscovery] ⚠️ NAME MISMATCH: Requested "${artistName}" but got "${providerData.name}"`);
            }
          } else {
            console.warn(`[DailyDiscovery] ❌ Provider found nothing for: "${artistName}"`);
          }
          
          if (providerData) {
            setRepresentativeArtist({
              name: artistName,
              images: providerData.images || []
            });
          }
        } catch (error) {
          console.log('Could not load representative artist');
        } finally {
          setLoadingArtistImage(false);
        }
      } else {
        setLoadingArtistImage(false);
      }

      // Scroll to top
      window.scrollTo({ top: 0, behavior: 'smooth' });

    } catch (error) {
      console.error('[Daily Discovery] Error loading discovery:', error);
      // In caso di errore, carica l'ultimo disponibile
      await loadTodayDiscovery();
    }
  };

  const handleDiscoverArtists = () => {
    if (!discovery || !discovery.artists) return;

    console.log('[DailyDiscovery] Navigating to artists, discovery ID:', discovery.id);

    // Naviga a ShowResult passando gli artisti come items con type 'daily-discovery-artists'
    // Gestisci sia array di stringhe (vecchio formato) che array di oggetti (nuovo formato)
    const mappedArtists = discovery.artists.map((artist, index) => {
      const artistName = typeof artist === 'string' ? artist : artist.name;
      const artistGenre = typeof artist === 'object' ? artist.genre : null;
      const artistOrigin = typeof artist === 'object' ? artist.origin : null;
      const artistSpotifyId = typeof artist === 'object'
        ? (artist.spotify_id || artist.spotifyId || null)
        : null;
      
      console.log(`[DailyDiscovery] Mapping artist ${index + 1}: "${artistName}" (genre: ${artistGenre}, origin: ${artistOrigin})`);
      
      return {
        name: artistName,
        genre: artistGenre,
        origin: artistOrigin,
        rank: index + 1,
        url: `https://www.last.fm/music/${encodeURIComponent(artistName)}`,
        spotifyId: artistSpotifyId,
        spotify_id: artistSpotifyId
      };
    });
    
    console.log(`[DailyDiscovery] Passing ${mappedArtists.length} artists to ShowResult:`, mappedArtists.map(a => a.name));
    
    navigate('/daily-discovery-artists', {
      state: {
        type: 'daily-discovery-artists',
        items: mappedArtists,
        query: discovery.title,
        fromDiscovery: true,
        discoveryId: discovery.id
      }
    });
  };

  if (!discovery) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: 'white',
        padding: '20px',
        textAlign: 'center'
      }}>
        <p>{t('dailyDiscovery.loading')}</p>
      </div>
    );
  }

  const artistImageUrl = representativeArtist?.images?.[0]?.url;

  // Formatta la data per visualizzazione
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return language === 'it' 
      ? date.toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })
      : date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  return (
    <div style={{
      minHeight: '100vh',
      paddingBottom: '40px',
      overflow: 'auto',
      position: 'relative'
    }}>
      <div style={{
        padding: '40px 20px 40px'
      }}>
        {/* Dropdown Selector per altri approfondimenti */}
        <div style={{
          maxWidth: '700px',
          margin: '0 auto 30px',
          position: 'relative'
        }}>
          <button
            onClick={() => setShowDropdown(!showDropdown)}
            style={{
              width: '100%',
              padding: '12px 20px',
              backgroundColor: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              color: 'white',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.1)';
            }}
          >
            <span>
              <i className="bi bi-list-ul" style={{ marginRight: '10px' }} />
              {t('dailyDiscovery.allDiscoveries')}
            </span>
            <i className={`bi bi-chevron-${showDropdown ? 'up' : 'down'}`} />
          </button>

          {/* Dropdown Menu */}
          {showDropdown && (
            <div style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              marginTop: '5px',
              backgroundColor: 'rgba(30,30,30,0.98)',
              border: '1px solid rgba(255,255,255,0.2)',
              borderRadius: '12px',
              maxHeight: '400px',
              overflowY: 'auto',
              zIndex: 1000,
              boxShadow: '0 8px 30px rgba(0,0,0,0.5)'
            }}>
              {loadingList ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                  {t('dailyDiscovery.loadingList')}
                </div>
              ) : allDiscoveries.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', color: 'rgba(255,255,255,0.6)' }}>
                  {t('dailyDiscovery.noOtherDiscoveries')}
                </div>
              ) : (
                allDiscoveries.map((disc, index) => (
                  <button
                    key={disc.id}
                    onClick={() => loadDiscoveryById(disc.id)}
                    style={{
                      width: '100%',
                      padding: '15px 20px',
                      backgroundColor: disc.id === discovery.id ? 'rgba(255,255,255,0.1)' : 'transparent',
                      border: 'none',
                      borderBottom: index < allDiscoveries.length - 1 ? '1px solid rgba(255,255,255,0.1)' : 'none',
                      color: 'white',
                      textAlign: 'left',
                      cursor: disc.id === discovery.id ? 'default' : 'pointer',
                      transition: 'background-color 0.2s ease',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px'
                    }}
                    onMouseEnter={(e) => {
                      if (disc.id !== discovery.id) {
                        e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.08)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (disc.id !== discovery.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span style={{
                      minWidth: '30px',
                      height: '30px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: '600',
                      color: 'rgba(255,255,255,0.6)'
                    }}>
                      {allDiscoveries.length - index}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ 
                        fontSize: '14px', 
                        fontWeight: '500',
                        marginBottom: '4px',
                        color: disc.id === discovery.id ? 'white' : 'rgba(255,255,255,0.9)'
                      }}>
                        {disc.title}
                      </div>
                      <div style={{ 
                        fontSize: '12px', 
                        color: 'rgba(255,255,255,0.5)' 
                      }}>
                        {formatDate(disc.discovery_date)} • {disc.artist_count || 0} {language === 'it' ? 'artisti' : 'artists'}
                      </div>
                    </div>
                    {disc.id === discovery.id && (
                      <i className="bi bi-check-circle-fill" style={{ 
                        color: 'rgba(255,255,255,0.7)',
                        fontSize: '16px' 
                      }} />
                    )}
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Immagine artista rappresentativo */}
        <div style={{
          width: '200px',
          height: '200px',
          margin: '0 auto 30px',
          position: 'relative'
        }}>
          <div style={{
            width: '100%',
            height: '100%',
            borderRadius: '50%',
            overflow: 'hidden',
            boxShadow: '0 8px 30px rgba(0,0,0,0.5)',
            backgroundColor: loadingArtistImage ? '#2a2a2a' : (artistImageUrl ? 'transparent' : '#555'),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            {artistImageUrl && !loadingArtistImage && (
              <img
                src={artistImageUrl}
                alt={representativeArtist.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover'
                }}
              />
            )}
          </div>
          {/* Spinning border quando sta caricando */}
          {loadingArtistImage && (
            <div className="spinning-border" />
          )}
        </div>

        {/* Titolo */}
        <h1 style={{
          fontSize: 'clamp(24px, 5vw, 38px)',
          fontWeight: '700',
          marginBottom: '10px',
          textAlign: 'center',
          color: 'white',
          lineHeight: '1.2',
          padding: '0 10px'
        }}>
          {discovery.title}
        </h1>

        {/* Tags */}
        {discovery.tags && discovery.tags.length > 0 && (
          <div style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'center',
            marginTop: '20px',
            marginBottom: '30px',
            flexWrap: 'wrap',
            padding: '0 10px'
          }}>
            {discovery.tags.map((tag, i) => (
              <span key={i} style={{
                padding: '5px 15px',
                backgroundColor: 'rgba(255,255,255,0.08)',
                border: '1px solid rgba(255,255,255,0.1)',
                borderRadius: '20px',
                fontSize: '12px',
                color: 'rgba(255,255,255,0.6)'
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Introduzione */}
        <div style={{
          fontSize: '15px',
          lineHeight: '1.75',
          color: 'rgba(255,255,255,0.85)',
          textAlign: 'left',
          marginBottom: '40px',
          padding: '0 15px',
          maxWidth: '700px',
          margin: '0 auto 40px'
        }}>
          {(() => {
            const paragraphs = discovery.introduction.split('\n\n');
            // Rimuovi il primo paragrafo se contiene il titolo o pattern come "n/n Titolo"
            const filteredParagraphs = paragraphs.filter((para, index) => {
              if (index === 0) {
                const paraTrimmed = para.trim();
                const titleTrimmed = discovery.title.trim();
                // Se il primo paragrafo è identico al titolo o inizia con "n/n" seguito dal titolo, rimuovilo
                if (paraTrimmed === titleTrimmed || 
                    /^\d+\/\d+\s+/.test(paraTrimmed) ||
                    paraTrimmed.startsWith(titleTrimmed + ':') ||
                    paraTrimmed.startsWith(titleTrimmed + '.')) {
                  return false;
                }
              }
              return true;
            });
            
            return filteredParagraphs.map((paragraph, index) => (
              <p key={index} style={{ marginBottom: '20px', marginTop: index === 0 ? 0 : 0 }}>
                {paragraph}
              </p>
            ));
          })()}
        </div>

        {/* Pulsante Discover Artists */}
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '40px',
          paddingBottom: '40px'
        }}>
          <button
            onClick={handleDiscoverArtists}
            style={{
              padding: '15px 40px',
              backgroundColor: 'white',
              color: '#121212',
              border: 'none',
              borderRadius: '30px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              boxShadow: '0 4px 20px rgba(255,255,255,0.2)',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = '0 6px 25px rgba(255,255,255,0.3)';
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = '0 4px 20px rgba(255,255,255,0.2)';
            }}
          >
            {t('dailyDiscovery.discoverArtists')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DailyDiscovery;
